/**
 * useMarkNotificationRead
 *
 * The notification-tap mark-read sequence, extracted from
 * `useInboxNotifications` (which owns the paginated fetch, the collapse/expand
 * windowing, the live subscription and the unread-badge predicate). Ports, in
 * order:
 *   1. the `!read` gate + awaited remote write
 *      (`push_navigation_service.dart:357-359` —
 *      `if (!notification.info.read) await ...setNotificationRead(notification)`);
 *   2. the flip-on-success-only optimistic local update
 *      (`messages_screen.dart:665-672`, which Flutter reaches only after the
 *      awaited write returns — a throwing write propagates and skips the flip);
 *   3. the badge re-check (`messages_screen.dart:673`) — automatic via the
 *      caller's `hasUnreadNotification` memo, which re-derives off the committed
 *      list.
 *
 * The optimistic flip is MANDATORY, not an optimization: the live subscription
 * filters `info.timestamp >= sinceTimestamp` where the cursor is the first
 * page's most-recent notification (`messages_screen.dart:324-327`), so every
 * older — i.e. nearly every tappable — notification is outside the query and its
 * `info.read` write produces NO `modified` echo for this client.
 *
 * This hook lives in the presentation layer (never in `InboxPage` — the page is
 * pure wiring/UI per `presentation.md` "Page + feature hook split"; it only
 * calls the callback the composing hook exposes).
 *
 * `markNotificationReadLocally` is intentionally NOT returned — no caller may
 * flip the local `read` state while bypassing the gate + remote write.
 */
import { useCallback } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import { setNotificationReadUseCase } from '@/domain/usecases/notifications/setNotificationReadUseCase';

/**
 * Optimistic local `read` flip for ONE list. Flutter mutates the shared
 * `AppNotification` in place (`messages_screen.dart:666`); the web entities are
 * treated as immutable, so the entry is replaced by a copy with
 * `info.read === true`.
 *
 * Returns the SAME array reference when no entry matched, so the caller can skip
 * that list's `setState` (and React skips the commit) entirely.
 */
function applyLocalReadFlip(
  list: AppNotificationEntity[],
  notificationId: string,
): AppNotificationEntity[] {
  let changed = false;
  const next = list.map(n => {
    if (n.id !== notificationId || n.info.read === true) return n;
    changed = true;
    return { ...n, info: { ...n.info, read: true } };
  });
  return changed ? next : list;
}

export interface UseMarkNotificationReadParams {
  /** Live truth for the visible list (the owner hook's ref), read + written in lockstep with `setNotifications`. */
  notificationsRef: MutableRefObject<AppNotificationEntity[]>;
  setNotifications: Dispatch<SetStateAction<AppNotificationEntity[]>>;
  /** Live truth for the collapsed cache, read + written in lockstep with `setCollapsedNotifications`. */
  collapsedNotificationsRef: MutableRefObject<AppNotificationEntity[]>;
  setCollapsedNotifications: Dispatch<SetStateAction<AppNotificationEntity[]>>;
}

export function useMarkNotificationRead(
  params: UseMarkNotificationReadParams,
): (notification: AppNotificationEntity) => Promise<void> {
  const {
    notificationsRef,
    setNotifications,
    collapsedNotificationsRef,
    setCollapsedNotifications,
  } = params;

  /**
   * Optimistic local `read` flip. Flutter: `messages_screen.dart:665-672`
   * (`notification.info.read = true` + a `setState` on the next microtask).
   *
   * Folds over BOTH lists because Flutter mutates the shared `AppNotification`
   * object, so whichever list holds it (`_notifications` or the collapsed cache)
   * observes the new value. Each list writes its ref and its state in lockstep —
   * the same discipline `collapseNotifications` / the collapsed-refill branch of
   * `getNotifications` use — so a concurrent live batch in
   * `applyNotificationChanges` folds against the POST-flip lists instead of
   * clobbering the flip. A list whose contents did not change keeps its identical
   * array reference and is not committed.
   *
   * The unread badge needs no explicit re-check (Flutter's
   * `_checkNotificationsUpdatesBadge`, `messages_screen.dart:673/574-585`): the
   * owner hook's `hasUnreadNotification` memo recomputes off the new
   * `notifications`.
   *
   * Deliberately NOT returned from this hook, so no caller can flip the local
   * `read` state while bypassing the gate + remote write.
   */
  const markNotificationReadLocally = useCallback(
    (notificationId: string): void => {
      if (!notificationId) return;

      const nextVisible = applyLocalReadFlip(notificationsRef.current, notificationId);
      if (nextVisible !== notificationsRef.current) {
        notificationsRef.current = nextVisible;
        setNotifications(nextVisible);
      }

      const nextCollapsed = applyLocalReadFlip(
        collapsedNotificationsRef.current,
        notificationId,
      );
      if (nextCollapsed !== collapsedNotificationsRef.current) {
        collapsedNotificationsRef.current = nextCollapsed;
        setCollapsedNotifications(nextCollapsed);
      }
    },
    [notificationsRef, setNotifications, collapsedNotificationsRef, setCollapsedNotifications],
  );

  /**
   * Mark a tapped notification read. Ports Flutter's tap sequence, in order:
   *   1. the `!read` gate + awaited remote write
   *      (`push_navigation_service.dart:357-359`) — an already-read notification
   *      issues no write and no flip;
   *   2. the optimistic local flip (`messages_screen.dart:665-672`), which
   *      Flutter reaches only if the awaited write did not throw — so on web the
   *      flip runs ONLY when the use case reported success;
   *   3. the badge re-check (`messages_screen.dart:673`) — automatic via the
   *      owner hook's `hasUnreadNotification` memo.
   *
   * `read === true` is the exact equivalent of Flutter's `!notification.info.read`
   * given `bool read = false` is non-null in Flutter (`notification_info.dart:31`)
   * while the web entity's `read` is optional (undefined = unread).
   */
  const markNotificationRead = useCallback(
    async (notification: AppNotificationEntity): Promise<void> => {
      // push_navigation_service.dart:357
      if (notification.info.read === true) return;

      // push_navigation_service.dart:358
      const ok = await setNotificationReadUseCase.execute(notification);

      // messages_screen.dart:665-672 — flip on success only.
      if (ok && notification.id) {
        markNotificationReadLocally(notification.id);
      }
    },
    [markNotificationReadLocally],
  );

  return markNotificationRead;
}
